import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { LogOut, User as UserIcon, Search, ShieldAlert, BookOpen, Newspaper, Menu, X } from "lucide-react";
import { ShareMenu } from "@/components/ShareMenu";

export function NavBar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Chiudi il menu mobile su ogni cambio rotta
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const navLinks = (
    <>
      <button
        onClick={() => navigate("/cerca")}
        data-testid="nav-search-link"
        className="font-ui text-sm text-[#1A362D] hover:text-[#C05C45] transition-colors flex items-center gap-1.5"
      >
        <Search size={16} /> Scuole
      </button>
      <Link
        to="/prodotti"
        data-testid="nav-products-link"
        className="font-ui text-sm text-[#1A362D] hover:text-[#C05C45] transition-colors flex items-center gap-1.5"
      >
        <BookOpen size={16} /> Libri & Giochi
      </Link>
      <Link
        to="/blog"
        data-testid="nav-blog-link"
        className="font-ui text-sm text-[#1A362D] hover:text-[#C05C45] transition-colors flex items-center gap-1.5"
      >
        <Newspaper size={16} /> Blog Papà dito
      </Link>
      <Link
        to="/info/privacy"
        data-testid="nav-privacy-link"
        className="font-ui text-sm text-[#5C6B64] hover:text-[#1A362D] transition-colors"
      >
        Privacy
      </Link>
    </>
  );

  return (
    <nav className="w-full border-b border-[#E8E6E1] bg-[#FAF9F6]/90 backdrop-blur-md sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/" data-testid="nav-home-link" className="flex items-center gap-2">
            <img src="/logo.png" alt="Per manina" className="w-9 h-9 rounded-full object-cover" />
            <span className="font-display text-2xl font-semibold text-[#1A362D] tracking-tight">Per manina</span>
          </Link>
          <ShareMenu
            type="home"
            slug=""
            title="Per manina — Scuole dell'infanzia, libri e giochi educativi raccontati dai genitori"
            compact
            className="!p-2 !rounded-full !bg-transparent hover:!bg-[#F2EFEB] !text-[#5C6B64] hover:!text-[#1A362D] !border-0"
          />
        </div>

        {/* DESKTOP */}
        <div className="hidden md:flex items-center gap-5">
          {navLinks}
          {user?.is_admin && (
            <Link to="/admin/segnalazioni" data-testid="nav-admin-link" className="font-ui text-sm text-[#C05C45] hover:text-[#A64A35] inline-flex items-center gap-1">
              <ShieldAlert size={14} /> Moderazione
            </Link>
          )}
          {user ? (
            <div className="flex items-center gap-3">
              <Link to="/area-personale" data-testid="nav-personal-area-link" className="flex items-center gap-2 font-ui text-sm text-[#1A362D] hover:text-[#C05C45]">
                {user.picture ? (
                  <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full object-cover border border-[#E8E6E1]" />
                ) : (
                  <UserIcon size={18} />
                )}
                <span className="hidden lg:inline">{user.name}</span>
              </Link>
              <button onClick={logout} data-testid="nav-logout-btn" className="text-[#8A9691] hover:text-[#B33A3A] transition-colors" title="Esci">
                <LogOut size={18} />
              </button>
            </div>
          ) : (
            <button onClick={handleLogin} data-testid="nav-login-btn" className="btn-primary text-sm">Accedi con Google</button>
          )}
        </div>

        {/* MOBILE */}
        <div className="md:hidden flex items-center gap-3">
          {user && (
            <Link to="/area-personale" data-testid="nav-personal-area-link-mobile">
              {user.picture ? (
                <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full object-cover border border-[#E8E6E1]" />
              ) : (
                <UserIcon size={20} />
              )}
            </Link>
          )}
          <button
            onClick={() => setMobileOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            aria-label={mobileOpen ? "Chiudi menu" : "Apri menu"}
            aria-expanded={mobileOpen}
            className="p-2 -mr-2 text-[#1A362D] hover:text-[#C05C45] transition-colors"
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* MOBILE DROPDOWN */}
      {mobileOpen && (
        <div
          className="md:hidden border-t border-[#E8E6E1] bg-[#FAF9F6] shadow-lg"
          data-testid="nav-mobile-menu"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 flex flex-col gap-4">
            {navLinks}
            {user?.is_admin && (
              <Link to="/admin/segnalazioni" data-testid="nav-admin-link-mobile" className="font-ui text-sm text-[#C05C45] inline-flex items-center gap-1.5">
                <ShieldAlert size={14} /> Moderazione
              </Link>
            )}
            <div className="divider pt-4 mt-2">
              {user ? (
                <button
                  onClick={() => { setMobileOpen(false); logout(); }}
                  data-testid="nav-logout-btn-mobile"
                  className="font-ui text-sm text-[#8A9691] hover:text-[#B33A3A] inline-flex items-center gap-1.5"
                >
                  <LogOut size={16} /> Esci
                </button>
              ) : (
                <button
                  onClick={handleLogin}
                  data-testid="nav-login-btn-mobile"
                  className="btn-primary text-sm w-full"
                >
                  Accedi con Google
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
